import Select from "components/Elements/Selectv2";
import {
  HitCondition,
  MessageCondition,
  TrackerCondition,
  WUAttributeCondition,
} from "pages/FlowBuilderv2/Nodes/NodeData";
import { useEffect, useState } from "react";
import { useDebounce } from "react-use";
import {
  messageEventsCorelationWaitUntil,
  WaitUntilMessageProviderCorelation,
} from "reducers/flow-builder.reducer";
import ApiService from "services/api.service";
import { Workflow, EntityWithComputedFields } from "types/Workflow";
import { ConditionEditorError, errorToMessageMap } from "./ConditionEditor";

interface MessageEditorProps {
  errors?: {
    [ConditionEditorError.NO_WORKFLOW_SPECIFiED]: string;
  };
  showErrors?: boolean;
  condition: MessageCondition;
  onChange: React.Dispatch<
    React.SetStateAction<
      HitCondition | TrackerCondition | MessageCondition | WUAttributeCondition
    >
  >;
}

const MessageEditor = ({
  condition,
  errors,
  showErrors,
  onChange,
}: MessageEditorProps) => {
  const [workflowSearchQuery, setWorkflowSearchQuery] = useState("");
  const [workflowSearchQueryPage, setWorkflowSearchQueryPage] = useState(1);
  const [workflowSearchTotalPages, setWorkflowSearchTotalPages] = useState(1);
  const [availableWorkflows, setAvailableWorkflows] = useState<
    EntityWithComputedFields<Workflow>[]
  >([]);
  const [isWorkflowSearchLoading, setIsWorkflowSearchLoading] = useState(false);

  const [specMessageQuery, setSpecMessageQuery] = useState("");
  const [isSpecMessageQueryLoading, setIsSpecMessageQueryLoading] =
    useState(false);
  const [possibleMessages, setPossibleMessages] = useState<
    Record<string, { key: string; title: string }[]>
  >({});

  const loadAllMessages = async (channel: string, specWorkflowId: string) => {
    setIsSpecMessageQueryLoading(true);
    try {
      const { data } = await ApiService.get<
        {
          id: string;
          metadata: {
            channel: string;
            customName: string;
            destination: string;
            template: number;
          };
        }[]
      >({
        url: `/workflows/messages/${specWorkflowId}/${channel}`,
      });

      setPossibleMessages((prev) => {
        prev[`${channel};;${specWorkflowId}`] = data.map((el) => ({
          key: el.id,
          title: el.metadata.customName,
        }));

        return prev;
      });
    } catch (error) {
    } finally {
      setIsSpecMessageQueryLoading(false);
    }
  };

  const loadPossibleWorkflows = async () => {
    setIsWorkflowSearchLoading(true);
    try {
      const { data } = await ApiService.get<{
        data: EntityWithComputedFields<Workflow>[];
        totalPages: number;
      }>({
        url: `/workflows?take=12&skip=${
          (workflowSearchQueryPage - 1) * 12
        }&search=${workflowSearchQuery}&orderBy=latestSave&orderType=desc&filterStatuses=Active,Paused,Draft`,
      });

      setAvailableWorkflows(
        workflowSearchQueryPage === 1
          ? data.data
          : [...availableWorkflows, ...data.data]
      );
      setWorkflowSearchTotalPages(data.totalPages);
    } catch (error) {
    } finally {
      setIsWorkflowSearchLoading(false);
    }
  };

  const searchableMessageInWorkflow = (channel: string, specId: string) => [
    {
      key: "ANY",
      title: `Any ${channel} in this workflow`,
    },
    ...(possibleMessages[`${channel};;${specId}`] || []),
  ];

  useEffect(() => {
    if (!condition.from) return;

    loadAllMessages(
      WaitUntilMessageProviderCorelation[condition.providerType],
      condition.from.key
    );
  }, [condition.from]);

  useDebounce(
    () => {
      setWorkflowSearchQueryPage(1);
      loadPossibleWorkflows();
    },
    500,
    [workflowSearchQuery]
  );

  useEffect(() => {
    loadPossibleWorkflows();
  }, [workflowSearchQueryPage]);

  return (
    <>
      <Select
        value={condition.from?.key}
        searchPlaceholder="search workflow"
        placeholder={condition.from?.title || "Select a workflow"}
        searchValue={workflowSearchQuery}
        isLoading={isWorkflowSearchLoading}
        onSearchValueChange={setWorkflowSearchQuery}
        onScrollToEnd={() => {
          if (
            isWorkflowSearchLoading ||
            workflowSearchQueryPage >= workflowSearchTotalPages
          )
            return;

          setWorkflowSearchQueryPage((prev) => prev + 1);
        }}
        onChange={(el, selectedOptionI) => {
          if (selectedOptionI === undefined || !el) return;

          onChange({
            ...condition,
            from: {
              key: el,
              title: (availableWorkflows?.map((availableJ) => ({
                key: availableJ.entity.id,
                title: availableJ.entity.name,
              })) || [])[selectedOptionI].title,
            },
          });
        }}
        noDataPlaceholder={"No results"}
        options={
          availableWorkflows?.map((el) => ({
            key: el.entity.id,
            title: el.entity.name,
          })) || []
        }
      />
      {condition.from && (
        <Select
          value={condition.fromSpecificMessage.key}
          placeholder={condition.fromSpecificMessage.title}
          searchValue={specMessageQuery}
          isLoading={isSpecMessageQueryLoading}
          onSearchValueChange={setSpecMessageQuery}
          onChange={(el, selectedOptionI) => {
            if (selectedOptionI === undefined) return;

            onChange({
              ...condition,
              fromSpecificMessage: {
                key: el,
                title: searchableMessageInWorkflow(
                  WaitUntilMessageProviderCorelation[condition.providerType],
                  condition.from!.key
                ).filter((specMsg) =>
                  specMsg?.title?.includes(specMessageQuery)
                )[selectedOptionI].title,
              },
            });
          }}
          options={searchableMessageInWorkflow(
            WaitUntilMessageProviderCorelation[condition.providerType],
            condition.from.key
          ).filter((el) => el?.title?.includes(specMessageQuery))}
        />
      )}
      <div className="flex gap-[10px]">
        <Select
          value={condition.eventCondition}
          onChange={(el) => {
            onChange({
              ...condition,
              eventCondition: el,
            });
          }}
          options={messageEventsCorelationWaitUntil[condition.providerType]}
        />
      </div>
      {showErrors &&
        errors &&
        errors[ConditionEditorError.NO_WORKFLOW_SPECIFiED] && (
          <div className="font-inter font-normal text-[12px] leading-5 text-[#E11D48]">
            {errorToMessageMap[ConditionEditorError.NO_WORKFLOW_SPECIFiED]}
          </div>
        )}
    </>
  );
};

export { MessageEditor };
